import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { ServiceArea, TeamServiceArea } from '@bringg/types';
import { action, observable, computed, makeObservable, runInAction } from 'mobx';
import { groupBy } from 'lodash';
import { CreateSpeedFactor } from '@bringg/dashboard-sdk/dist/SpeedFactor/Service/SpeedFactor.service';
import { promiseMemoOne } from '@bringg-frontend/utils';

import {
	ActionType,
	buildCreateSpeedFactorsOnDelete,
	buildCreateSpeedFactorsOnUpdate,
	CalendarEvent,
	buildCreateSpeedFactorPayload,
	validateFormData,
	validateSpeedFactorOnUpdate,
	EventFormData
} from 'bringg-web/features/service-area/traffic-matrix/data-utils';
import { SpeedFactorGroup } from 'bringg-web/features/service-area/traffic-matrix/speed-factor-grouping/types';
import { groupSpeedFactors } from 'bringg-web/features/service-area/traffic-matrix/speed-factor-grouping';

class ServiceAreasStore {
	serviceAreas: Map<number, ServiceArea> = new Map();
	teamsServiceAreas: Map<number, TeamServiceArea[]> = new Map();
	serviceAreasSpeedFactors: Map<number, SpeedFactorGroup[]> = new Map();
	serviceAreasGroupByTeam: Map<number, TeamServiceArea[]> = new Map();

	isFetched = false;
	isTeamsFetched = false;

	private _memoFetchSA = promiseMemoOne(async () => {
		const result = await getRootEnv().dashboardSdk.sdk.serviceAreas.getAll();
		this.serviceAreas = new Map(result.service_areas.map(sa => [sa.id, sa]));
		this.isFetched = true;
	});

	private _memoFetchTeamsSA = promiseMemoOne(async () => {
		const { teams_service_areas } = await getRootEnv().dashboardSdk.sdk.teamServiceAreas.getAll();

		this.serviceAreasGroupByTeam = new Map(
			Object.entries(groupBy(teams_service_areas, 'team_id')).map(([id, teamSA]) => [Number(id), teamSA])
		);

		this.teamsServiceAreas = new Map(
			Object.entries(groupBy(teams_service_areas, 'service_area_id')).map(([id, team]) => [Number(id), team])
		);

		this.isTeamsFetched = true;
	});

	constructor() {
		makeObservable(this, {
			serviceAreas: observable.shallow,
			teamsServiceAreas: observable.shallow,
			serviceAreasGroupByTeam: observable.ref,
			getAll: computed,
			allFetched: computed,
			fetchAll: action,
			forceFetchAll: action,
			fetchServiceAreas: action,
			fetchTeamsServiceAreas: action,
			isFetched: observable,
			isTeamsFetched: observable,
			updateAllTeamsServiceAreas: action,
			create: action,
			update: action,
			delete: action,

			/* Speed Factors */
			serviceAreasSpeedFactors: observable.shallow
		});
	}

	get getAll() {
		return Array.from(this.serviceAreas.values());
	}

	get allFetched() {
		return this.isFetched && this.isTeamsFetched;
	}

	async fetchAll() {
		await Promise.all([this.fetchServiceAreas(), this.fetchTeamsServiceAreas()]);
	}

	async forceFetchAll() {
		this.isFetched = false;
		this.isTeamsFetched = false;
		this._memoFetchSA.clearCache();
		this._memoFetchTeamsSA.clearCache();

		await this.fetchAll();
	}

	async fetchServiceAreas() {
		if (this.isFetched) {
			return;
		}

		return this._memoFetchSA();
	}

	async fetchTeamsServiceAreas() {
		if (this.isTeamsFetched) {
			return;
		}

		return this._memoFetchTeamsSA();
	}

	async updateAllTeamsServiceAreas(serviceAreaId: number, teamServiceAreas: Partial<TeamServiceArea>[] = []) {
		const { teams_service_areas } = await getRootEnv().dashboardSdk.sdk.teamServiceAreas.updateAll({
			service_area_id: serviceAreaId,
			team_service_areas: teamServiceAreas as TeamServiceArea[]
		});

		const existing = this.serviceAreas.get(serviceAreaId);
		this.serviceAreas.set(serviceAreaId, {
			...existing,
			team_ids: teamServiceAreas.map(team => team.team_id)
		});
		this.teamsServiceAreas.set(serviceAreaId, teams_service_areas);
		if (teamServiceAreas.length) {
			// we need to update team=>SA grouping for each selected team
			teamServiceAreas.forEach(teamServiceArea => {
				this.serviceAreasGroupByTeam.set(teamServiceArea.team_id, teams_service_areas);
			});
		} else {
			// in case teams was deleted we need to for through each team in serviceAreasGroupByTeam
			// and filter out serviceAreaId from teamCurrentServiceAreas
			this.serviceAreasGroupByTeam.forEach((teamCurrentServiceAreas, teamId) => {
				this.serviceAreasGroupByTeam.set(
					teamId,
					teamCurrentServiceAreas.filter(({ service_area_id }) => service_area_id !== serviceAreaId)
				);
			});
		}
		return teams_service_areas;
	}

	async create(serviceArea: Partial<ServiceArea>) {
		const { service_area } = await getRootEnv().dashboardSdk.sdk.serviceAreas.create({
			service_area: serviceArea
		});

		this.serviceAreas.set(service_area.id, service_area);
		return service_area;
	}

	async update(serviceArea: Partial<ServiceArea>) {
		const { service_area } = await getRootEnv().dashboardSdk.sdk.serviceAreas.update(serviceArea.id, {
			service_area: serviceArea
		});

		this.serviceAreas.set(service_area.id, service_area);
		return service_area;
	}

	async delete(id: number) {
		await getRootEnv().dashboardSdk.sdk.serviceAreas.delete(id);
		this.serviceAreas.delete(id);
	}

	/* Service Area Speed Factors*/
	async fetchSpeedFactors(serviceAreaId: number) {
		const result = await getRootEnv().dashboardSdk.sdk.speedFactor.list(serviceAreaId);

		if (result.success) {
			const speedFactors = groupSpeedFactors(result.read);

			runInAction(() => {
				this.serviceAreasSpeedFactors.set(serviceAreaId, speedFactors);
			});

			return speedFactors;
		}

		return [];
	}

	getSpeedFactors(serviceAreaId: number) {
		return this.serviceAreasSpeedFactors.get(serviceAreaId);
	}

	protected async createSpeedFactorAPI(payload: CreateSpeedFactor) {
		return getRootEnv().dashboardSdk.sdk.speedFactor.create(payload);
	}

	async createSpeedFactors(formData: EventFormData, timezone: string) {
		const result = validateFormData(formData);
		if (!result.valid) throw new Error(result.message);

		const payloads = buildCreateSpeedFactorPayload(formData, timezone);
		const promises = payloads.map(this.createSpeedFactorAPI.bind(this));

		await Promise.all(promises);

		return { message: 'Event successfully created!' };
	}

	async updateSpeedFactor(
		formData: EventFormData,
		event: CalendarEvent,
		type: ActionType,
		groupedSpeedFactors: SpeedFactorGroup[],
		timezone: string
	) {
		const result = validateSpeedFactorOnUpdate(event, formData);
		if (!result.valid) throw new Error(result.message);

		const payloads = buildCreateSpeedFactorsOnUpdate(event, groupedSpeedFactors, type, formData, timezone);
		const promises = payloads.map(this.createSpeedFactorAPI.bind(this));

		await this.deleteSpeedFactorsAPI(event.originalEvent);

		await Promise.all(promises);

		return { message: 'Events successfully updated!' };
	}

	private async deleteSpeedFactorsAPI(speedFactor: { original_uuid: string }) {
		return getRootEnv().dashboardSdk.sdk.speedFactor.deleteSeries(speedFactor.original_uuid);
	}

	async deleteSpeedFactor(
		event: CalendarEvent,
		type: ActionType,
		groupedSpeedFactors: SpeedFactorGroup[],
		timezone: string
	) {
		const response = { message: 'All events successfully deleted!' };

		const speedFactor = event.originalEvent;

		if (type === 'all') {
			await this.deleteSpeedFactorsAPI(speedFactor);
		} else if (type === 'current' || type === 'current_and_following') {
			await this.deleteSpeedFactorsAPI(speedFactor);

			const payloads = buildCreateSpeedFactorsOnDelete(event, groupedSpeedFactors, type, timezone);
			const promises = payloads.map(this.createSpeedFactorAPI.bind(this));
			await Promise.all(promises);

			response.message = 'Event successfully deleted!';
		} else {
			throw Error('Not implemented');
		}

		return response;
	}
}

export default ServiceAreasStore;
